import type { ContactToExportPreview, CrmAccount } from 'domain/crm';

import type { Country } from './country';

export type Contact = {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  linkedin: string;
  country: string;
  role: string;
  site: string;
  originalSite: string;
  department: string;
  seniority: string;
  title: string;
  companyName: string;
  companyId: string;
  numericCompanyLinkedinIds?: number[];
  hasValidEmails: boolean;
  hasMobilePhoneNumbers: boolean;
  hasDirectPhoneNumbers: boolean;
  isRevealed: boolean;
  privateData?: PrivateData;
  matchedProfiles: string[];
  accuracyGrade?: string;
  salesforceContactUrl?: string;
  salesforceLeadUrl?: string;
  hubspotContactUrl?: string;
  salesforceExportedContactId?: string;
  salesforceExportedLeadId?: string;
  hubspotExportedContactId?: string;

  // collected data
  linkedinId?: string;
  linkedinSalesNavId?: string;
  companyNames: string[];
  companyLinkedinIds: string[];
};

export type PrivateData = {
  id: string;
  phoneNumbers?: {
    score: number;
    number: string;
    numberType: string;
    internalType: 'mobile' | 'direct';
    isDoNotCall?: boolean;
  }[];
  emails?: string[];
  linkedInUrl: string;
  requester?: { userId: number; accountId: number; name: string; email: string };
  lastUpdated?: string;
  revealErrors?: {
    hasValidEmailsError: boolean;
    hasDirectPhoneNumbersError: boolean;
    hasMobilePhoneNumbersError: boolean;
  };
};

export type RevealBulkResponse = {
  contacts: PrivateData[];
  requestId: string;
  notRevealedContactsCount?: number;
  notRevealedContactsIds?: string[];
  revealedForFreeCount?: number;
  totalCount?: number;
  uniqueCount?: number;
};

export type PrivateDataType = 'email' | 'direct-phone' | 'mobile-phone';

export type EnrichedContactWithCrm = Contact & {
  crmAccount?: CrmAccount;
  preview?: ContactToExportPreview;
};

export type ContactVariant = 'listItem' | 'singular';

export enum ContactDetailApiKey {
  Email = 'hasEmail',
  MobilePhone = 'hasMobilePhone',
  DirectPhone = 'hasDirectPhone',
  EmailAndMobilePhone = 'hasEmailAndMobilePhone',
}

export interface ContactDetailsFilterDto {
  hasMobilePhone?: boolean;
  hasEmail?: boolean;
  hasDirectPhone?: boolean;
  hasEmailAndMobilePhone?: boolean;
}

export type ContactStatusFilterValue = Partial<{
  notRevealed: boolean;
  exported: boolean;
  notExported: boolean;
}>;

export type ContactsCountByCountry = { country: Country; contactsCount: number };

export const hasPrivateData = ({ privateData }: Contact): boolean =>
  Boolean(privateData?.emails?.length || privateData?.phoneNumbers?.length);

export const isEmptyContactFilter = (contactFilter: ContactDetailsFilterDto): boolean => {
  return Object.values(contactFilter).every((filterValue: boolean) => !filterValue);
};

export const hasDataToReveal = ({
  hasDirectPhoneNumbers,
  hasMobilePhoneNumbers,
  hasValidEmails,
}: Partial<Contact>): boolean => Boolean(hasDirectPhoneNumbers || hasMobilePhoneNumbers || hasValidEmails);

export const revealContact = (contact: Contact, privateData: PrivateData | undefined): Contact => {
  return { ...contact, isRevealed: true, privateData };
};

export const hasMatchedProfiles = (contact: Contact): boolean =>
  contact.matchedProfiles && contact.matchedProfiles.length > 0;

export const mergeContact = (
  contact: Contact | undefined | null,
  collectedContactInfo: Partial<Contact> | undefined
): Contact | undefined => {
  if (!contact) {
    return undefined;
  }
  const mergedContact = { ...contact };

  if (collectedContactInfo?.country) {
    mergedContact.country = collectedContactInfo.country;
  }

  if (collectedContactInfo?.title) {
    mergedContact.title = collectedContactInfo.title;
  }

  return mergedContact;
};

export const createContact = (collectedContactInfo: Partial<Contact> | undefined): Partial<Contact> => {
  const contact = {
    name: '',
    title: '',
    country: '',
    matchedProfiles: [],
    hasDirectPhoneNumbers: false,
    hasMobilePhoneNumbers: false,
    hasValidEmails: false,
  };

  if (collectedContactInfo?.name) {
    contact.name = collectedContactInfo.name;
  }

  if (collectedContactInfo?.country) {
    contact.country = collectedContactInfo.country;
  }

  if (collectedContactInfo?.title) {
    contact.title = collectedContactInfo.title;
  }

  return contact;
};
